import { FIRE_EVENT_ID } from "../events/eventDefinitions.ts";
import { buildHexGrid, isInHexShape } from "../../models/hex/hexGrid.ts";
import {
  isTileTerrain,
  tileKey,
  type TileCoordinates,
  type TileData,
  type TileKey,
  type TileTerrain,
} from "../../models/worldState.ts";

export const MAP_DEFINITION_VERSION_V1 = 1 as const;
export const MAP_DEFINITION_VERSION = 2 as const;

export type MapDefinitionVersion =
  | typeof MAP_DEFINITION_VERSION_V1
  | typeof MAP_DEFINITION_VERSION;

const KNOWN_TIMELINE_EVENT_IDS = new Set<string>([FIRE_EVENT_ID]);

/** Event ids accepted by `startEvent` timeline beats (map parse + editor palette). */
export function getKnownTimelineEventIds(): readonly string[] {
  return [...KNOWN_TIMELINE_EVENT_IDS];
}

export interface MapDefinitionTile {
  q: number;
  r: number;
  terrain: TileTerrain | null;
}

export interface MapTimelineNotifyBeat {
  verb: "notify";
  atTurn: number;
  messageKey?: string;
  fallback: { title: string; body: string };
}

export interface MapTimelineStartEventBeat {
  verb: "startEvent";
  atTurn: number;
  eventId: string;
  tile?: TileCoordinates;
}

export type MapTimelineBeat = MapTimelineNotifyBeat | MapTimelineStartEventBeat;

export interface MapDefinitionFile {
  version: MapDefinitionVersion;
  radius: number;
  tiles: MapDefinitionTile[];
  timeline?: MapTimelineBeat[];
  randomEvents?: boolean;
}

export type WorldMapScenario = MapDefinitionFile;

/** Minimal tile list accepted by `worldTilesFromMapDefinition` (parser output or session preload). */
export type MapScenarioTiles = Pick<MapDefinitionFile, "radius" | "tiles">;

export function areRandomEventsEnabled(def: MapDefinitionFile): boolean {
  if (def.timeline === undefined || def.timeline.length === 0) {
    return true;
  }
  return def.randomEvents === true;
}

function isSupportedMapVersion(value: unknown): value is MapDefinitionVersion {
  return value === MAP_DEFINITION_VERSION_V1 || value === MAP_DEFINITION_VERSION;
}

function parseTimelineBeat(
  entry: unknown,
  radius: number,
  index: number,
): MapTimelineBeat {
  if (typeof entry !== "object" || entry === null) {
    throw new Error(`Invalid timeline beat at index ${index}`);
  }

  const beat = entry as Record<string, unknown>;
  const verb = beat.verb;
  const atTurn = beat.atTurn;

  if (typeof atTurn !== "number" || !Number.isInteger(atTurn) || atTurn < 1) {
    throw new Error(`Invalid timeline atTurn at index ${index}`);
  }

  if (verb === "notify") {
    const fallback = beat.fallback;
    if (typeof fallback !== "object" || fallback === null) {
      throw new Error(`Invalid notify fallback at index ${index}`);
    }
    const fallbackRecord = fallback as Record<string, unknown>;
    if (
      typeof fallbackRecord.title !== "string" ||
      typeof fallbackRecord.body !== "string"
    ) {
      throw new Error(`Invalid notify fallback at index ${index}`);
    }

    const notifyBeat: MapTimelineNotifyBeat = {
      verb: "notify",
      atTurn,
      fallback: {
        title: fallbackRecord.title,
        body: fallbackRecord.body,
      },
    };

    if (beat.messageKey !== undefined) {
      if (typeof beat.messageKey !== "string" || beat.messageKey.length === 0) {
        throw new Error(`Invalid notify messageKey at index ${index}`);
      }
      notifyBeat.messageKey = beat.messageKey;
    }

    return notifyBeat;
  }

  if (verb === "startEvent") {
    if (typeof beat.eventId !== "string" || beat.eventId.length === 0) {
      throw new Error(`Invalid startEvent eventId at index ${index}`);
    }
    if (!KNOWN_TIMELINE_EVENT_IDS.has(beat.eventId)) {
      throw new Error(`Unknown timeline event id: ${beat.eventId}`);
    }

    const startBeat: MapTimelineStartEventBeat = {
      verb: "startEvent",
      atTurn,
      eventId: beat.eventId,
    };

    if (beat.tile !== undefined) {
      if (typeof beat.tile !== "object" || beat.tile === null) {
        throw new Error(`Invalid startEvent tile at index ${index}`);
      }
      const tile = beat.tile as Record<string, unknown>;
      const q = tile.q;
      const r = tile.r;
      if (
        typeof q !== "number" ||
        typeof r !== "number" ||
        !Number.isInteger(q) ||
        !Number.isInteger(r)
      ) {
        throw new Error(`Invalid startEvent tile at index ${index}`);
      }
      if (!isInHexShape(q, r, radius)) {
        throw new Error(
          `Timeline tile (${q}, ${r}) is outside map radius ${radius}`,
        );
      }
      startBeat.tile = { q, r };
    } else if (beat.eventId === FIRE_EVENT_ID) {
      throw new Error(`startEvent "${beat.eventId}" requires a tile`);
    }

    return startBeat;
  }

  throw new Error(`Unknown timeline verb at index ${index}`);
}

export function parseMapDefinition(json: string): MapDefinitionFile {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    throw new Error("Invalid map JSON");
  }

  if (
    typeof parsed !== "object" ||
    parsed === null ||
    !("version" in parsed) ||
    !("radius" in parsed) ||
    !("tiles" in parsed)
  ) {
    throw new Error("Invalid map definition shape");
  }

  const record = parsed as Record<string, unknown>;

  if (!isSupportedMapVersion(record.version)) {
    throw new Error(`Unsupported map version: ${String(record.version)}`);
  }

  if (
    typeof record.radius !== "number" ||
    !Number.isInteger(record.radius) ||
    record.radius < 1
  ) {
    throw new Error("Invalid map radius");
  }
  const radius = record.radius;

  if (!Array.isArray(record.tiles)) {
    throw new Error("Invalid map tiles");
  }

  const tiles: MapDefinitionTile[] = [];

  for (const entry of record.tiles) {
    if (typeof entry !== "object" || entry === null) {
      throw new Error("Invalid tile entry");
    }

    const tile = entry as Record<string, unknown>;
    const q = tile.q;
    const r = tile.r;
    const terrain = tile.terrain;

    if (
      typeof q !== "number" ||
      typeof r !== "number" ||
      !Number.isInteger(q) ||
      !Number.isInteger(r)
    ) {
      throw new Error("Invalid tile coordinates");
    }

    if (!isInHexShape(q, r, radius)) {
      throw new Error(`Tile (${q}, ${r}) is outside map radius ${radius}`);
    }

    if (terrain !== null && !isTileTerrain(terrain)) {
      throw new Error(`Unknown terrain: ${String(terrain)}`);
    }

    tiles.push({ q, r, terrain: terrain ?? null });
  }

  const result: MapDefinitionFile = {
    version: record.version,
    radius,
    tiles,
  };

  if (record.version === MAP_DEFINITION_VERSION_V1) {
    if (record.timeline !== undefined || record.randomEvents !== undefined) {
      throw new Error("timeline fields require map version 2");
    }
    return result;
  }

  if (record.randomEvents !== undefined) {
    if (typeof record.randomEvents !== "boolean") {
      throw new Error("Invalid randomEvents flag");
    }
    result.randomEvents = record.randomEvents;
  }

  if (record.timeline !== undefined) {
    if (!Array.isArray(record.timeline)) {
      throw new Error("Invalid timeline");
    }
    result.timeline = record.timeline.map((beat, index) =>
      parseTimelineBeat(beat, radius, index),
    );
  }

  return result;
}

/** Builds world tiles from a map definition (null terrain → plains, all hidden). */
export function worldTilesFromMapDefinition(
  def: MapScenarioTiles,
): Record<TileKey, TileData> {
  const tiles: Record<TileKey, TileData> = {};
  const terrainByKey = new Map<string, TileTerrain | null>();

  for (const tile of def.tiles) {
    terrainByKey.set(tileKey(tile.q, tile.r), tile.terrain);
  }

  for (const { q, r } of buildHexGrid(def.radius)) {
    const key = tileKey(q, r);
    const terrain = terrainByKey.get(key) ?? null;
    tiles[key] = {
      q,
      r,
      terrain: terrain ?? "plains",
      visibility: "hidden",
      isStartingTile: false,
      foodDepletion: 0,
    };
  }

  return tiles;
}
